import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, TextField, Grid, Typography, IconButton,
  MenuItem, FormControlLabel, Checkbox, Paper, Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { FormFillCustomField } from '../../types/client';

interface FormFillCustomFieldsBuilderProps {
  fields: FormFillCustomField[];
  onChange: (fields: FormFillCustomField[]) => void;
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' }
];

/**
 * Converts a display name to a field_key (snake_case)
 * e.g., "Monthly Hiring" -> "monthly_hiring"
 */
const toFieldKey = (displayName: string): string => {
  return displayName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_');
};

const FormFillCustomFieldsBuilder: React.FC<FormFillCustomFieldsBuilderProps> = ({
  fields,
  onChange
}) => {
  // Local state for dropdown options text inputs (keyed by field index)
  // This allows typing semicolons/spaces without immediate parsing
  const [dropdownInputs, setDropdownInputs] = useState<Record<number, string>>({});

  // Initialize dropdown inputs from fields on mount or when fields change externally
  useEffect(() => {
    const initialInputs: Record<number, string> = {};
    fields.forEach((field, index) => {
      if (field.field_type === 'dropdown' && field.dropdown_options) {
        initialInputs[index] = field.dropdown_options.join('; ');
      }
    });
    setDropdownInputs(initialInputs);
  }, [fields.length]); // Only re-sync when field count changes

  // Parse dropdown options string into array (semicolon-separated to allow commas in values)
  const parseDropdownOptions = useCallback((optionsString: string): string[] => {
    return optionsString
      .split(';')
      .map(opt => opt.trim())
      .filter(opt => opt.length > 0);
  }, []);

  const handleAddField = () => {
    const newField: FormFillCustomField = {
      field_key: '',
      display_name: '',
      field_type: 'text',
      is_required: false
    };
    onChange([...fields, newField]);
  };

  const handleRemoveField = (index: number) => {
    const updatedFields = fields.filter((_, i) => i !== index);
    // Clean up dropdown inputs state - shift indices down
    const newDropdownInputs: Record<number, string> = {};
    Object.keys(dropdownInputs).forEach(key => {
      const keyIndex = parseInt(key, 10);
      if (keyIndex < index) {
        newDropdownInputs[keyIndex] = dropdownInputs[keyIndex];
      } else if (keyIndex > index) {
        newDropdownInputs[keyIndex - 1] = dropdownInputs[keyIndex];
      }
    });
    setDropdownInputs(newDropdownInputs);
    onChange(updatedFields);
  };

  const handleFieldChange = (index: number, key: keyof FormFillCustomField, value: any) => {
    const updatedFields = [...fields];
    const field = { ...updatedFields[index] };
    
    // Auto-generate field_key from display_name
    if (key === 'display_name') {
      field.display_name = value;
      field.field_key = toFieldKey(value);
    } else if (key === 'field_type') {
      field.field_type = value;
      // Clear dropdown options if switching away from dropdown
      if (value !== 'dropdown') {
        delete field.dropdown_options;
        // Clean up local input state
        setDropdownInputs(prev => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
      } else {
        field.dropdown_options = field.dropdown_options || [];
        // Initialize local input state
        setDropdownInputs(prev => ({
          ...prev,
          [index]: field.dropdown_options?.join('; ') || ''
        }));
      }
    } else if (key === 'is_required') {
      field.is_required = value;
    }
    
    updatedFields[index] = field;
    onChange(updatedFields);
  };

  // Handle local dropdown input change - just update local state
  const handleDropdownInputChange = (index: number, value: string) => {
    setDropdownInputs(prev => ({
      ...prev,
      [index]: value
    }));
  };

  // Sync dropdown options to parent on blur - parse the string into array
  const handleDropdownInputBlur = (index: number) => {
    const inputValue = dropdownInputs[index] || '';
    const options = parseDropdownOptions(inputValue);
    
    const updatedFields = [...fields];
    const field = { ...updatedFields[index] };
    field.dropdown_options = options;
    updatedFields[index] = field;
    onChange(updatedFields);
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Configure custom fields to capture from website form submissions. 
        The "Display Name" should match the field label as it appears in the form emails.
      </Typography>
      
      {fields.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', mb: 2 }}>
          <Typography color="textSecondary">
            No custom fields configured. Click "Add Field" to create one.
          </Typography>
        </Paper>
      ) : (
        fields.map((field, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Display Name"
                  value={field.display_name}
                  onChange={(e) => handleFieldChange(index, 'display_name', e.target.value)}
                  placeholder="e.g., Monthly Hiring"
                  helperText={field.field_key ? `Key: ${field.field_key}` : 'Auto-generated key'}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Field Type"
                  value={field.field_type}
                  onChange={(e) => handleFieldChange(index, 'field_type', e.target.value)}
                >
                  {FIELD_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              {field.field_type === 'dropdown' && (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Dropdown Options"
                    value={dropdownInputs[index] ?? field.dropdown_options?.join('; ') ?? ''}
                    onChange={(e) => handleDropdownInputChange(index, e.target.value)}
                    onBlur={() => handleDropdownInputBlur(index)}
                    placeholder="50,000 - 1,00,000; 1,00,001 - 1,50,000"
                    helperText="Semicolon-separated options (allows commas in values)"
                  />
                </Grid>
              )}
              
              <Grid item xs={field.field_type === 'dropdown' ? 6 : 12} md={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={field.is_required}
                      onChange={(e) => handleFieldChange(index, 'is_required', e.target.checked)}
                    />
                  }
                  label="Required"
                />
              </Grid>
              
              <Grid item xs={6} md={1}>
                <IconButton
                  color="error"
                  onClick={() => handleRemoveField(index)}
                  title="Remove field"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
            
            {/* Preview of dropdown options as chips */}
            {field.field_type === 'dropdown' && field.dropdown_options && field.dropdown_options.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                  Options:
                </Typography>
                {field.dropdown_options.map((opt, i) => (
                  <Chip key={i} label={opt} size="small" variant="outlined" />
                ))}
              </Box>
            )}
          </Paper>
        ))
      )}
      
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddField}
        sx={{
          borderColor: 'var(--e-global-color-secondary)',
          color: 'var(--e-global-color-secondary)',
          '&:hover': {
            backgroundColor: 'var(--e-global-color-secondary)',
            color: '#fff',
            borderColor: 'var(--e-global-color-secondary)'
          }
        }}
      >
        Add Field
      </Button>
    </Box>
  );
};

export default FormFillCustomFieldsBuilder;
